// 相似图裂变
import { defaultBatchSize } from "../default";
import { ModuleEnum } from "../moduleEnum";
import type { LayoutReferState } from "@/types/image";

export const module = ModuleEnum["fission"];

export const fissionParams = {
  module,
  layoutReferId: null as null | number, // 原始稿件图片id
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  imageGuidanceWeights: 2, // 画面指导权重
  aspectRatios: "0:0", // 尺寸比例
  batchSize: defaultBatchSize, // 生成数量
  fissionReferType: 1, // 1-默认参考，2-构图参考
  prompt: "", // 用户输入的提示词
  imgTags: []
};

import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { getModelEventTrackType } from "../index";

// 将当前用户输入的生图参数转换成后端所需要
export async function getFissionGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  let params = {
    module: fissionParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: artworkStore.artworkState.modelList,
    loraList: artworkStore.artworkState.modelList.map((item) => {
      return {
        modelId: item.id,
        strength: item.strength
      };
    })
  } as any;

  params = {
    ...artworkStore.fission.params,
    ...params,
    source: 1,
    layoutReferId: artworkStore.fission.params.layoutRefer?.id
  };

  // 图片提示词
  params.imgTag = artworkStore.fission.params.imgTags.map((item) => item.enName).join(",");

  // 使用默认参考图时，移除提示词
  if (params.fissionReferType === 1) {
    params.prompt = "";
    params.imgTag = "";
  }

  return params;
}

import { useEventTracking } from "@/hooks/useEventTracking";
import type { ModelState } from "@/types/model";
import { startModuleGenerate } from "./index";
import { useModelStore } from "@/store/model";
import { styleModelOptions } from "@/constants";
import { getModuleLabelById } from "@xmiles/holopix-helper";

// 开启模块生图
export async function startFissionGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = await getFissionGenerateParam();
  const module = fissionParams.module;
  const modelList = artworkStore.artworkState.modelList || [];
  const modelLabels = modelList.flatMap((item) => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every((i) => i.userId !== 0)) return "是";
    if (modelList.every((i) => i.userId === 0)) return "否";
    return "混合";
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  const modelNameList = modelList.map((i: any) => i.modelName) || [];
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => item.loraType ? loraTypeMap[item.loraType] || '视频模型' : item.loraType);

  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    useEventTracking({
      event: "creation_event",
      eventValue: {
        client_id: clientId,
        module: getModuleLabelById(module) as string,
        source: params.source === 1 ? "直接生成" : "再迭代",
        artwork_id: artworkStore.artworkState.id.toString(),
        batchSize: params.batchSize,
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        hp_model_id: String(modelIdList.join(",")),
        model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
        is_personal_model: getModelEventTrackType(params.modelList),
        model_tags: modelTagList.join(","),
        model_type: modelTypeList.join(","),

        hp_model_id_list: modelIdList,
        hp_model_name_list: modelNameList,
        hp_model_tags_list: modelTagList,
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText,
        is_composite_model: loraTypeLabels
      }
    });
  }

  function reportFinishEventTracking({
    client_id = "",
    result = 0 as 0 | 1,
    imageIds = "",
    queue_time_number = 0,
    loading_time_number = 0,
    vipChannel = false
  } = {}) {
    useEventTracking({
      event: "artwork_status_event",
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id,
        source: params.source === 1 ? "直接生成" : "再迭代",
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        hp_model_id: String(modelIdList.join(",")),
        model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
        first_model_ckpt: params.modelList[0]?.ckptType,
        vipChannel: vipChannel,
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
        is_personal_model: getModelEventTrackType(params.modelList),

        prompt: String(params.prompt),
        model_tags: modelTagList.join(","),
        model_type: modelTypeList.join(","),

        hp_model_id_list: modelIdList,
        hp_model_name_list: modelNameList,
        hp_model_tags_list: modelTagList,
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText,
        is_composite_model: loraTypeLabels
      }
    });
  }
}

export const referenceOptions = [
  {
    label: "默认参考（强）",
    value: 1,
    guidKey: "defaultReference"
  },
  {
    label: "构图参考（弱）",
    value: 2,
    guidKey: "compositionReference"
  }
];
